import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useSprints = (userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: sprints, isLoading } = useQuery({
    queryKey: ['sprints', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('prompt_architecture_sprints')
        .select(`
          *,
          sprint_contributions(*)
        `)
        .or(`creator_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  const createSprint = useMutation({
    mutationFn: async (sprint: {
      objective: string;
      creator_id: string;
    }) => {
      const { data, error } = await supabase
        .from('prompt_architecture_sprints')
        .insert(sprint)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
      toast({
        title: "Sprint Created",
        description: "Your prompt architecture sprint has been created.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create sprint",
        variant: "destructive",
      });
    },
  });

  const updateSprint = useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('prompt_architecture_sprints')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update sprint",
        variant: "destructive",
      });
    },
  });

  const addContribution = useMutation({
    mutationFn: async (contribution: {
      sprint_id: string;
      contributor_id: string;
      stage: string;
      contribution_text: string;
    }) => {
      const { data, error } = await supabase
        .from('sprint_contributions')
        .insert(contribution)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add contribution",
        variant: "destructive",
      });
    },
  });

  const deleteSprint = useMutation({
    mutationFn: async (sprintId: string) => {
      const { error } = await supabase
        .from('prompt_architecture_sprints')
        .delete()
        .eq('id', sprintId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
      queryClient.invalidateQueries({ queryKey: ['completed-sprints'] });
      toast({
        title: "Sprint Deleted",
        description: "The sprint has been permanently deleted.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete sprint",
        variant: "destructive",
      });
    },
  });

  return {
    sprints,
    isLoading,
    createSprint: createSprint.mutate,
    updateSprint: updateSprint.mutate,
    addContribution: addContribution.mutate,
    deleteSprint: deleteSprint.mutate,
  };
};
